import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import immutable from 'immutable';
import classes from 'classnames';
import 'styles/utils/NestedSelect.styl';

/**
 * Custom 'Option' component to be passed to NestedSelect in props.optionComponent.
 * Heavily based on the 'Option' component in react-select v0.9.1.
 *
 * Modified to allow nested options and custom event handlers
 */
class CustomOption extends React.Component {
  blockEvent(event) {
    event.preventDefault();
    if (event.target.tagName !== 'A' || !('href' in event.target)) {
      return;
    }

    if (event.target.target) {
      window.open(event.target.href);
    } else {
      window.location.href = event.target.href;
    }
  }

  handleMouseDown(e) {
    // stop propagation so that a click doesn't trigger a parent option select
    e.stopPropagation();
    this.props.mouseDown(this.props.option, e);
  }

  /** Return the `left` of the nested-options element */
  leftOfNestedOptions(position, optionsBoundingRect) {
    return position.left - optionsBoundingRect.width;
  }

  /**
   * Return the `left` css string for the nested-options element
   * @param position {object} Bounding rect of `optionDOMNode`
   * @param optionsBoundingRect {object} Bounding rect of the nested options
   */
  leftStyleOfNestedOptions(position, optionsBoundingRect) {
    return `left: ${this.leftOfNestedOptions(position, optionsBoundingRect)}px`;
  }

  /** Return the css string that's used to render nested options to the left of the parent option */
  cssStringForNestedOptions(position, optionsBoundingRect) {
    const topStyle = `top: ${position.top}px`;
    const leftStyle = this.leftStyleOfNestedOptions(position, optionsBoundingRect);
    const cssString = `${topStyle}; ${leftStyle};`;
    return cssString;
  }

  updateNestedOptionsPosition(children) {
    // handle entering an option with nested options
    if (children.length > 0) {
      // get the option position relative to its positioned parent
      const optionDOMNode = ReactDOM.findDOMNode(this);
      const position = optionDOMNode.getBoundingClientRect();
      const children = immutable.List(optionDOMNode.children);
      const nestedOptions = children.find(element => element.className === 'nested-options');
      const optionsBoundingRect = nestedOptions.getBoundingClientRect();

      // update inline style
      const cssString = this.cssStringForNestedOptions(position, optionsBoundingRect);
      this.nestedOptions.setAttribute('style', cssString);
    }
  }

  updateNestedOptionsPositionInMouseEvents() {
    const children = this.props.option.children || [];
    this.updateNestedOptionsPosition(children);
  }

  handleMouseEnter(e) {
    this.updateNestedOptionsPositionInMouseEvents();
    this.props.mouseEnter(this.props.option, e);
  }

  handleMouseOver() {
    this.updateNestedOptionsPositionInMouseEvents();
  }

  handleMouseMove() {
    this.updateNestedOptionsPositionInMouseEvents();
  }

  handleMouseLeave(e) {
    this.props.mouseLeave(this.props.option, e);
  }

  render() {
    let option = this.props.option;
    let label = option.create
      ? this.props.addLabelText.replace('{label}', option.label)
      : this.props.renderFunc(option);

    // Get an array of CustomOption elements for each nested option
    let children = option.children || [];
    let childrenElements = Object.keys(children).map(function (key) {
      let op = children[key];
      let optionClass = classes({
        'Select-option': true,
        'is-disabled': op.disabled,
      });
      let optionResult = React.createElement(CustomOption, {
        key: 'option-' + op['value'],
        className: optionClass,
        renderFunc: this.props.renderFunc,
        mouseDown: this.props.mouseDown,
        mouseEnter: this.props.mouseEnter,
        mouseLeave: this.props.mouseLeave,
        addLabelText: this.props.addLabelText,
        option: op,
      });
      return optionResult;
    }, this);

    // Add 'parent' class to options with nested options
    let optionClasses = classes(this.props.className, option.className, {
      parent: children.length > 0,
    });

    return option.disabled ? (
      <div className={optionClasses} onMouseDown={this.blockEvent} onClick={this.blockEvent}>
        {label}
      </div>
    ) : (
      <div
        className={optionClasses}
        style={option.style}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onMouseMove={this.handleMouseMove}
        onMouseOver={this.handleMouseOver}
        onClick={this.handleMouseDown}
        title={option.title}>
        {label}
        {(() => {
          // render arrow
          if (childrenElements.length > 0) {
            return <div className="parent-arrow"></div>;
          }
        })()}
        {(() => {
          // render nested options, if any
          if (childrenElements.length > 0) {
            return (
              <div
                className="nested-options"
                ref={c => {
                  this.nestedOptions = c;
                }}>
                {childrenElements}
              </div>
            );
          }
        })()}
      </div>
    );
  }
}

CustomOption.propTypes = {
  addLabelText: PropTypes.string, // string rendered in case of allowCreate option passed to ReactSelect
  className: PropTypes.string, // className (based on mouse position)
  mouseDown: PropTypes.func, // method to handle click on option element
  mouseEnter: PropTypes.func, // method to handle mouseEnter on option element
  mouseLeave: PropTypes.func, // method to handle mouseLeave on option element
  option: PropTypes.object.isRequired, // object that is base for that option
  renderFunc: PropTypes.func, // method passed to ReactSelect component to render label text
};

/**
 * Higher Order Component over react-select that allows for nested options.
 * It has a custom Option component and filterOptions method
 */
class NestedSelect extends React.Component {
  /**
   * When the user clears the select input box, we force the parent component to reload
   * and thus allowing us to start from a good state again to perform searches
   */
  onInputChange = inputValue => {
    if (inputValue === '') {
      this.props.forceNestedSelectPropsReload();
    }
  };

  /**
   * Filter the list of options to show based on the current values and input text.
   * It assumes multi-selection is enabled and ignores case of options.
   */
  filterOptions = (options, filterValue, exclude, parentValue = null) => {
    let filterOption = (res, op) => {
      // filter children
      let children = op.children || [];
      let filteredChildren = [];
      if (children.length > 0) {
        filteredChildren = this.filterOptions(children, filterValue, exclude, op['value']);
        op.children = filteredChildren;
      }

      // check if the option should be visible
      let showOption = false;
      if (exclude.indexOf(op['value']) > -1) {
        showOption = false;
      } else {
        let valueTest = String(op['value']);
        let labelTest = String(op['label']);
        valueTest = valueTest.toLowerCase();
        labelTest = labelTest.toLowerCase();
        filterValue = filterValue.toLowerCase();
        showOption = !filterValue
          ? true
          : valueTest.indexOf(filterValue) >= 0 || labelTest.indexOf(filterValue) >= 0;
      }

      // add option to result
      if (showOption || filteredChildren.length > 0) {
        if (parentValue && !op['value'].includes(parentValue)) {
          op['value'] = parentValue + ' / ' + op['value'];
        }
        res.push(op);
      }
      return res;
    };

    return (options || []).reduce(filterOption, []);
  };

  render() {
    const className = classes('nested-select', this.props.className);

    return (
      <Select
        {...this.props}
        onInputChange={this.onInputChange}
        className={className}
        filterOptions={this.filterOptions}
        optionComponent={CustomOption}
      />
    );
  }
}

export default NestedSelect;
